import {
  articleTrackLabel,
  articleUrl,
  border,
  ChevronLink,
  FaqCategory,
  HELP_LIST_TRACK_CAT,
  hidden,
  LinkWithHelpHighlight,
  SearchBarContainer,
} from '../util';
import React from 'react';
import { ContentBody, ContentTitle } from './common';
import styled from 'styled-components';
import {
  IconChevronLeft,
  IconChevronRight,
  Type,
  screenMdMin,
  spacer24,
} from '@spotify-internal/encore-web';
import { SearchBar } from '../../Search';
import { useT, useLocale } from '@spotify-internal/i18n-core';

interface FaqCategoryViewType {
  category?: FaqCategory;
}

const BackButton = styled.div`
  margin-bottom: ${spacer24};
  @media (min-width: ${screenMdMin}) {
    ${hidden}
  }
`;

const Content = styled(ContentBody)`
  max-width: unset;
`;

// it seems the li needs to be styled, or else the ul inside will have a strange error
const ArticleList = styled.ul`
  list-style: none;
  padding-left: 0;
`;

const ArticleLinkContainer = styled.li`
  ${border}
  padding: ${spacer24} 0;

  &:last-child {
    border-bottom: none;

    @media (max-width: ${screenMdMin}) {
      ${border}
    }
  }

  @media (max-width: ${screenMdMin}) {
    &:first-child {
      ${border}
    }
  }
`;

const ArticleLink = styled(LinkWithHelpHighlight)`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;

  svg {
    min-width: ${spacer24};
    margin-left: ${spacer24};
  }
`;

export const StyledIconChevronRight = styled(IconChevronRight)`
  align-self: center;
  [dir='rtl'] & {
    transform: rotateY(180deg);
  }
`;

const LinkText = styled(Type).attrs({
  as: Type.span,
  variant: 'cello',
})``;

export const FaqCategoryView: React.FC<FaqCategoryViewType> = ({
  category,
}) => {
  const t = useT();
  const locale = useLocale();
  const filteredFaqs =
    !locale || locale === 'en'
      ? category?.faqs
      : category?.faqs.filter((i: any) => i.translated);
  return (
    <Content>
      <BackButton>
        <ChevronLink
          aria-label={t(
            'FRODOR_CONTENT_BACK_BUTTON',
            'Back',
            'Back means previous in this case',
          )}
          track={[HELP_LIST_TRACK_CAT, 'Back to Home']}
          href="/help"
        >
          <IconChevronLeft aria-hidden="true" />
        </ChevronLink>
      </BackButton>

      <SearchBarContainer data-testid="help-search-bar">
        <SearchBar
          contentTypes={['faq']}
          placeholder={t('FRODOR_e6f5ed', 'Search help articles', '')}
          suggestCount={5}
          trackCategory={HELP_LIST_TRACK_CAT}
          getSuggestUrl={sug =>
            `${sug.url}?ref=sug_${HELP_LIST_TRACK_CAT}${
              category ? `&category=${category.urlSlug}` : ''
            }`
          }
        />
      </SearchBarContainer>

      <ContentTitle>{category?.name}</ContentTitle>
      <ArticleList>
        {(filteredFaqs || []).map((article, i) => (
          <ArticleLinkContainer key={i}>
            <ArticleLink
              track={[HELP_LIST_TRACK_CAT, articleTrackLabel(article)]}
              href={articleUrl(article, category)}
            >
              <LinkText>{article.question}</LinkText>
              <StyledIconChevronRight />
            </ArticleLink>
          </ArticleLinkContainer>
        ))}
      </ArticleList>
    </Content>
  );
};
